import {
  getNameWithInitialsReversed,
  MaterialResponse,
  UUID,
  StageResponse,
  ActForm,
  DocumentMeta,
  UnitLabel,
  ActsWithoutHeaders,
} from '@builddocs/data-management'
import {
  ActFormKeys,
  Application,
  Certificate,
  ExecutiveDrawing,
  ExecutiveScheme,
  Laboratory,
  Normative,
  Permits,
  Supporting,
  WorkingDraft,
} from '@builddocs/filestore-api'
import format from 'date-fns/format'
import isMatch from 'date-fns/isMatch'
import { ru } from 'date-fns/locale'
import parseISO from 'date-fns/parseISO'

import {
  AOSRJsonType,
  ManualDocsType,
  SecondaryActJsonType,
} from '../Types/dataJSON'
import {
  ActSectionBase,
  IActTemplate,
  IFrontPageSection,
  IHeaderActSection,
  IInlineActSection,
  IPageSeparatorActSection,
  ISelectActSection,
  ISubHeaderActSection,
  ITableActSection,
  ITextActSection,
  ITextTemplateActSection,
  RowDataType,
  TableHeaderType,
} from '../viewer/template.type'

import { fieldForAddress } from './getEditField'

export type AssigneeType = {
  journalAssigneeId?: UUID
  ogrn?: string
  physicalAddress?: { unrestrictedValue: string }
  organizationName?: string
  employmentId: UUID
  userId?: UUID
  firstName: string
  lastName: string
  middleName: string
  organizationAddress?: string
  organizationInn?: string
  inn?: string
  organizationOgrn?: string
  organizationValue?: string
  positionDescription: string
}

type GetDocValue =
  | [Laboratory, 'laboratories']
  | [ExecutiveScheme, 'executiveSchemes']
  | [ExecutiveDrawing, 'executiveDrawings']
  | [Normative, 'normatives']
  | [Normative, 'normativeDocs']
  | [WorkingDraft, 'workingDrafts']
  | [Certificate, 'certificates']
  | [WorkingDraft, 'projectDocs']
  | [Supporting, 'supportingDocs']
  | [Permits, 'permits']
  | [undefined, string]
  | [Supporting, 'supportingDocsForWorks']

function getDocValue(...args: GetDocValue): string {
  const [doc, type] = args

  if (!doc) return ''

  switch (type) {
    case 'normatives':
    case 'normativeDocs':
    case 'supportingDocs':
    case 'supportingDocsForWorks':
    case 'permits':
      return `${doc?.name} ${'number' in doc && doc?.number}`
    case 'workingDrafts':
    case 'projectDocs':
      doc['documentType'] = Application.WORKING_DRAFT
      if (doc.documentType === Application.WORKING_DRAFT) {
        return `${doc?.name} ${doc?.number ?? ''} ${
          doc?.issueDate
            ? `от ${format(parseISO(doc.issueDate), 'd MMMM yyyy г.', {
                locale: ru,
              })}`
            : ''
        } ${doc.sheet ? `лист ${doc.sheet}` : ''} ${
          doc.cipher ? `шифр ${doc.cipher}` : ''
        } ${doc?.organization ?? ''}`
      } else {
        return ''
      }
    case 'laboratories':
      return `${doc.name} ${'number' in doc ? doc.number : ''} ${
        doc.issueDate
          ? `от ${format(parseISO(doc.issueDate), 'd MMMM yyyy г.', {
              locale: ru,
            })}`
          : ''
      } ${doc.organization ? `организация ${doc.organization}` : ''}`
    case 'certificates':
    case 'executiveSchemes':
    case 'executiveDrawings':
      doc['documentType'] = Application.EXECUTIVE_SCHEME
      if (doc.documentType === Application.EXECUTIVE_SCHEME) {
        return `${doc?.name} ${doc?.number ?? ''} ${
          doc.issueDate
            ? `от ${format(parseISO(doc.issueDate), 'd MMMM yyyy г.', {
                locale: ru,
              })}`
            : ''
        } ${doc.sheet ? `лист ${doc.sheet}` : ''} ${
          doc.cipher
            ? `${type === 'executiveSchemes' ? 'номер документа' : 'шифр'} ${
                doc.cipher
              }`
            : ''
        }`
      } else {
        return ''
      }
    default:
      return ''
  }
}

const buildStringFromArray = <T>(
  array: T[],
  itemGetter: (item: T) => string,
  separator?: string,
) => array?.map(itemGetter).join(separator ?? ', ')

type GetDocs = [ManualDocsType, string]

const getDocs = (...args: GetDocs): string => {
  const [docs, type] = args

  if (docs?.manualField?.[type] && Array.isArray(docs.manualField?.[type])) {
    const foo = docs.manualField[type] ?? []

    // TODO: Получилось громоздко с getDocValue, но ещё надо девести до ума
    return buildStringFromArray(foo, doc => getDocValue(doc as any, type) || '')
  } else {
    return ''
  }
}
const getMaterialList = (
  materialsObj: {
    materials: MaterialResponse[]
    selectedMaterials: UUID[]
    value: string
  },
  actType?: ActFormKeys,
): string => {
  return materialsObj?.selectedMaterials
    ? buildStringFromArray(
        materialsObj.selectedMaterials?.reduce(
          (acc: MaterialResponse[], id) => [...acc, materialsObj.materials[id]],
          [],
        ),
        item => {
          // Для AVR_FSK не показываем количество и единицу измерения
          if (actType === 'AVR_FSK') {
            return `${item.materialName} производитель ${
              item.manufacturer
            } (${getAllCertificateInfo(item.documents)})`
          }
          return `${item.materialName} производитель ${
            item.manufacturer
          } (${getAllCertificateInfo(item.documents)}) ${item.amount} ${
            UnitLabel[item.unit]
          }`
        },
      )
    : (materialsObj?.value as string)
}

const getAllCertificateInfo = (documents: DocumentMeta[]) => {
  return documents
    .filter(d => d.documentType === 'CERTIFICATE')
    .map(d =>
      [
        `Сертификат ${d.documentName}`,
        `${
          d.issueDate
            ? `от ${new Date(d.issueDate).toLocaleDateString('ru-RU')}`
            : ''
        }`,
        `${
          d.expirationDate
            ? `до ${new Date(d.expirationDate).toLocaleDateString('ru-RU')}`
            : ''
        }`,
      ].join(' '),
    )
    .join(', ')
}

const getWorkList = (worksObj: {
  works: StageResponse[]
  selectedWorks: UUID[]
  value: string
}): string => {
  return worksObj?.selectedWorks
    ? buildStringFromArray(
        worksObj.selectedWorks?.reduce(
          (acc: StageResponse[], id) => [...acc, worksObj.works[id]],
          [],
        ),
        item => `${item?.title}, ${item?.place}`,
      )
    : (worksObj?.value as string)
}

const getOrganizationValue = (org: {
  manualOrganization?: {
    inn: string
    ogrn: string
    name: string
    physicalAddress: { unrestrictedValue: string }
  }
  value?: string
}) =>
  org?.value
    ? org.value
    : org?.manualOrganization
    ? `${org.manualOrganization.name}, ИНН ${org.manualOrganization.inn}, ОГРН ${org.manualOrganization?.ogrn}, ${org.manualOrganization?.physicalAddress?.unrestrictedValue}`
    : ''

const getAssignees = ({
  assignees,
  key,
  forceIncludeOrganization,
  template,
  actType,
}: {
  assignees:
    | {
        manualAssignees:
          | Record<
              UUID,
              AssigneeType & {
                organizationName: string
                physicalAddress: { unrestrictedValue: string }
                ogrn: string
              }
            >
          | undefined
        value: string
      }
    | { assignees: AssigneeType[]; value: string }
  key: string
  forceIncludeOrganization?: string[]
  template?: IActTemplate
  actType?: ActFormKeys
}) => {
  if (!assignees) {
    return ''
  }
  const listAssignees =
    'manualAssignees' in assignees && assignees.manualAssignees
      ? Object.values(assignees.manualAssignees)
      : 'assignees' in assignees
      ? assignees.assignees
      : assignees.value

  return Array.isArray(listAssignees)
    ? listAssignees
        .map(assignee => {
          if (forceIncludeOrganization) {
            if (forceIncludeOrganization.includes(key)) {
              return `${
                assignee.positionDescription
              } ${getNameWithInitialsReversed(assignee)} ${
                assignee?.organizationName ?? ''
              }, ИНН ${assignee.inn ?? ''}, ОГРН ${assignee?.ogrn ?? ''}, ${
                assignee.physicalAddress?.unrestrictedValue ?? 'Организация'
              }, Приказ.`
            } else {
              return `${
                assignee.positionDescription
              } ${getNameWithInitialsReversed(assignee)}`
            }
          } else {
            if (actType && ActsWithoutHeaders.has(actType)) {
              return `${
                assignee.positionDescription
              } ${getNameWithInitialsReversed(assignee)} ${
                assignee?.organizationName ?? ''
              }, ИНН ${assignee.inn ?? ''}, ОГРН ${assignee?.ogrn ?? ''}, ${
                assignee.physicalAddress?.unrestrictedValue ?? 'Организация'
              }`
            }

            if (
              template &&
              template.content.some(
                section =>
                  section.key ===
                  key.replace('representative_', '').replace('_inspector', ''),
              )
            ) {
              return `${
                assignee.positionDescription
              } ${getNameWithInitialsReversed(assignee)}`
            } else {
              return `${
                assignee.positionDescription
              } ${getNameWithInitialsReversed(assignee)} ${
                assignee?.organizationName ?? ''
              }, ИНН ${assignee.inn ?? ''}, ОГРН ${assignee?.ogrn ?? ''}, ${
                assignee.physicalAddress?.unrestrictedValue ?? 'Организация'
              }`
            }
          }
        })
        .join(' ')
    : listAssignees
}

const getAssigneeSign = (assigness: {
  manualAssignees: Record<UUID, AssigneeType> | undefined
  value: string
}) =>
  assigness?.manualAssignees
    ? Object.values(assigness.manualAssignees)
        .map(assignee => getNameWithInitialsReversed(assignee))
        .join(', ')
    : assigness?.value

export const getValueFieldForSecondary = ({
  value,
  dataJSON,
  sectionTemplate,
  template,
  shouldShowMaterials,
  shouldShowLaboratories,
  shouldShowExecutiveSchemes,
}: {
  value: string | RowDataType[][]
  dataJSON: SecondaryActJsonType
  sectionTemplate?:
    | IHeaderActSection
    | IInlineActSection
    | ITextTemplateActSection
    | ITableActSection
    | IFrontPageSection
    | ISelectActSection
    | IPageSeparatorActSection
    | ITextActSection
    | ISubHeaderActSection
    | TableHeaderType
    | ActSectionBase
  template?: IActTemplate
  shouldShowMaterials?: boolean
  shouldShowLaboratories?: boolean
  shouldShowExecutiveSchemes?: boolean
}): string => {
  if (
    !(
      sectionTemplate &&
      'dataType' in sectionTemplate &&
      sectionTemplate.dataType
    )
  ) {
    return value as string
  }
  switch (sectionTemplate.dataType) {
    case 'OTHER':
      if (
        'conclusion_3' in dataJSON ||
        'conclusion_2' in dataJSON ||
        'conclusion_1' in dataJSON
      ) {
        return `а) разрешается использование строительных конструкций по назначению${
          dataJSON.conclusion_1?.value
            ? `, б) разрешается использование строительных конструкций по назначению с нагружением в размере ${dataJSON.conclusion_1?.value}% проектной нагрузки`
            : ''
        }${
          dataJSON.conclusion_2?.value
            ? `, в) разрешается полное нагружение при выполнении следующих условий: ${dataJSON.conclusion_2?.value}`
            : ''
        }${
          dataJSON.conclusion_3?.value
            ? `, г) разрешается производство последующих работ: ${dataJSON.conclusion_3?.value}`
            : ''
        }`
      } else {
        return ''
      }

    case 'DOCUMENTS':
      if ('keysForValue' in sectionTemplate && sectionTemplate.keysForValue) {
        return buildStringFromArray(
          sectionTemplate.keysForValue.filter(key => key !== 'applications'),
          key =>
            getValueFieldForSecondary({
              value: '',
              dataJSON,
              sectionTemplate:
                template && template.content.find(item => item.key === key),
            }),
        )
      } else {
        const showHidden = sectionTemplate.showHidden
        const documentFields = Object.values(fieldForAddress).filter(
          (field, index, fields) => fields.indexOf(field) === index,
        )

        return 'documentTypes' in sectionTemplate &&
          sectionTemplate.documentTypes &&
          dataJSON[sectionTemplate.key] &&
          'manualField' in dataJSON[sectionTemplate.key]
          ? showHidden
            ? documentFields
                .map(field => getDocs(dataJSON[sectionTemplate.key], field))
                .filter(Boolean)
                .join(', ')
            : sectionTemplate.documentTypes?.includes(Application.LABORATORY) &&
              sectionTemplate.key === 'documents_laboratories'
            ? shouldShowLaboratories
              ? sectionTemplate.documentTypes
                  .map(type =>
                    getDocs(
                      dataJSON[sectionTemplate.key],
                      fieldForAddress[type],
                    ),
                  )
                  .filter(Boolean)
                  .join(', ')
              : 'Лаборатории указаны в актах освидетельствования скрытых работ'
            : sectionTemplate.documentTypes?.includes(
                Application.EXECUTIVE_SCHEME,
              ) && sectionTemplate.key === 'documents_executive_schemes'
            ? shouldShowExecutiveSchemes
              ? sectionTemplate.documentTypes
                  .map(type =>
                    getDocs(
                      dataJSON[sectionTemplate.key],
                      fieldForAddress[type],
                    ),
                  )
                  .filter(Boolean)
                  .join(', ')
              : 'Исполнительные схемы и чертежи указаны в актах освидетельствования скрытых работ'
            : sectionTemplate.documentTypes
                .map(type =>
                  getDocs(dataJSON[sectionTemplate.key], fieldForAddress[type]),
                )
                .filter(Boolean)
                .join(', ')
                .replace(/, /g, '')
          : (value as string)
      }

    case 'MATERIAL':
      return (dataJSON.act_type === ActForm.AOOK ||
        dataJSON.act_type === ActForm.APOOK ||
        dataJSON.act_type === ActForm.ANMO) &&
        !shouldShowMaterials
        ? 'Материалы указаны в актах освидетельствования скрытых работ'
        : getMaterialList(dataJSON[sectionTemplate.key], dataJSON.act_type)

    case 'WORK':
      return getWorkList(dataJSON[sectionTemplate.key])

    case 'ASSIGNEE':
      return getAssignees({
        assignees: dataJSON[sectionTemplate.key + ''],
        key: sectionTemplate.key + '',
        template,
        actType: dataJSON.act_type,
      })

    case 'SIGN':
      return getAssigneeSign(dataJSON[sectionTemplate.key])

    case 'ORGANIZATION':
      return getOrganizationValue(dataJSON[sectionTemplate.key])

    case 'DATE':
      return isMatch(value as string, 'yyyy-MM-dd')
        ? format(parseISO(value as string), 'd MMMM yyyy г.', { locale: ru })
        : (value as string)

    default:
      return value as string
  }
}

export const getValueFieldForAOSR = ({
  value,
  dataJSON,
  sectionTemplate,
  showCertAOSR,
  template,
  forceIncludeOrganization,
  shouldIncludeSupportingsInProjectDocuments,
}: {
  value: string | RowDataType[][]
  dataJSON: AOSRJsonType
  sectionTemplate?:
    | IHeaderActSection
    | IInlineActSection
    | ITextTemplateActSection
    | ITableActSection
    | IFrontPageSection
    | ISelectActSection
    | IPageSeparatorActSection
    | ITextActSection
    | ISubHeaderActSection
    | TableHeaderType
  showCertAOSR?: boolean | undefined
  template?: IActTemplate
  forceIncludeOrganization: string[] | undefined
  shouldIncludeSupportingsInProjectDocuments: boolean
}): string => {
  const completedWorks =
    dataJSON.acceptance_object && 'completedWorks' in dataJSON.acceptance_object
      ? dataJSON.acceptance_object?.completedWorks
      : undefined

  switch (sectionTemplate?.key) {
    case 'acceptance_object':
      return completedWorks
        ? buildStringFromArray(
            completedWorks.works,
            item => `${item?.title} ${item?.place}`,
          )
        : (value as string)

    case 'representative_developer_inspector':
    case 'representative_executor_building':
    case 'representative_executor_building_inspector':
    case 'representative_executor_works':
    case 'representative_other':
    case 'representative_designer':
      return getAssignees({
        assignees: dataJSON[sectionTemplate.key],
        key: sectionTemplate.key,
        forceIncludeOrganization,
        template,
      })

    case 'project_documents':
      return `${
        completedWorks
          ? completedWorks.works
              .map(item =>
                buildStringFromArray(
                  item.projectDocIds,
                  id =>
                    getDocValue(completedWorks.projectDocs[id], 'projectDocs'),
                  ' ',
                ),
              )
              .join(', ')
          : (value as string)
      }${
        shouldIncludeSupportingsInProjectDocuments &&
        dataJSON.documents_for_work?.supportingDocsForWorksIds?.length > 0
          ? `, ${buildStringFromArray(
              dataJSON.documents_for_work.supportingDocsForWorksIds,
              id =>
                getDocValue(
                  dataJSON.documents_for_work.supportingDocsForWorks[id],
                  'supportingDocsForWorks',
                ),
              ', ',
            )}`
          : ''
      }
          `

    case 'documents_for_work':
      return `${
        completedWorks
          ? completedWorks.works
              .map(item =>
                buildStringFromArray(
                  item.normativeDocIds,
                  id =>
                    getDocValue(
                      completedWorks.normativeDocs[id],
                      'normativeDocs',
                    ),
                  ' ',
                ),
              )
              .join(', ')
          : (value as string)
      }${
        dataJSON.documents_for_work?.supportingDocsForWorksIds?.length > 0
          ? `, ${buildStringFromArray(
              dataJSON.documents_for_work.supportingDocsForWorksIds,
              id =>
                getDocValue(
                  dataJSON.documents_for_work.supportingDocsForWorks[id],
                  'supportingDocsForWorks',
                ),
              ', ',
            )}`
          : ''
      }`

    case 'material':
      return completedWorks
        ? Object.values(completedWorks.materials)
            .map(
              material =>
                `${material.materialName} (${buildStringFromArray(
                  material.certificateIds,
                  id =>
                    getDocValue(
                      completedWorks.certificates[id],
                      'certificates',
                    ),
                )})`,
            )
            .join(', ')
        : (value as string)

    case 'documents': {
      if (!completedWorks) {
        return value as string
      }

      const laboratories = buildStringFromArray(
        completedWorks.aosrDocs.laboratoryIds,
        id => getDocValue(completedWorks.laboratories[id], 'laboratories'),
      )

      const executiveSchemes = buildStringFromArray(
        completedWorks.aosrDocs.executiveSchemeIds,
        id =>
          getDocValue(completedWorks.executiveSchemes[id], 'executiveSchemes'),
      )

      const executiveDrawings = buildStringFromArray(
        completedWorks.aosrDocs.executiveDrawingIds,
        id =>
          getDocValue(
            completedWorks.executiveDrawings[id],
            'executiveDrawings',
          ),
      )

      return [laboratories, executiveSchemes, executiveDrawings]
        .filter(Boolean)
        .join(', ')
    }

    case 'applications':
      return `${
        completedWorks
          ? `${
              showCertAOSR
                ? `${buildStringFromArray(
                    Object.values(completedWorks.certificates),
                    doc => getDocValue(doc, 'certificates'),
                  )}`
                : ''
            }
        ${buildStringFromArray(
          Object.values(completedWorks.executiveSchemes),
          doc => getDocValue(doc, 'executiveSchemes'),
        )}
        ${buildStringFromArray(
          Object.values(completedWorks.laboratories),
          doc => getDocValue(doc, 'laboratories'),
        )}`
          : (value as string)
      }, ${
        dataJSON.applications?.supportingDocs
          ? buildStringFromArray(
              dataJSON.applications.supportingIds.map(
                id => dataJSON.applications.supportingDocs[id],
              ),
              doc => getDocValue(doc, 'supportingDocs'),
            )
          : ''
      }  `

    default:
      return value as string
  }
}
